import React, { useState, useEffect } from 'react';
import { BringzzButton, BringzzText } from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
    BringzzInput
} from "@bringzz/components";
import { toast } from 'react-toastify';

const AddCardForm = () => {
    const { backPress, navigate } = useNavigation();
    const [cardNumber, setCardNumber] = useState("")
    const [cardCVC, setCardCVC] = useState("")
    const [cardExpDate, setCardExpDate] = useState("")
    const [cardHolder, setCardHolder] = useState("")
    const [cardType, setCardType] = useState("")
    const [isFlipped, setIsFlipped] = useState(false)
    const [isCVCFocused, setIsCVCFocused] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const validateCard = () => {
        return !cardNumber || !cardExpDate || !cardCVC || !cardHolder
    }

    const formatCardNumber = (value) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
        const matches = v.match(/\d{4,16}/g);
        const match = matches && matches[0] || ''
        const parts = []

        for (let i = 0, len = match.length; i < len; i += 4) {
            parts.push(match.substring(i, i + 4))
        }

        if (parts.length) {
            return parts.join(' ')
        } else {
            return value
        }
    }

    const validateCardNumber = (number) => {
        const regex = {
            visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
            mastercard: /^5[1-5][0-9]{14}$/
        };

        const cardNumber = number.replace(/\s/g, '');
        
        if (regex.visa.test(cardNumber)) {
            setCardType('visa');
            return true;
        } else if (regex.mastercard.test(cardNumber)) {
            setCardType('mastercard');
            return true;
        }
        
        setCardType('');
        return false;
    }

    const handleSave = async () => {
        if (validateCard()) return;
        
        try {
            setIsSaving(true);
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            toast.success('Credit card added successfully');
            navigate('/payment');
        } catch (error) {
            console.error('Failed to save card:', error);
            toast.error('Failed to add credit card');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCVCChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 4) {
            setCardCVC(value);
        }
    };

    useEffect(() => {
        // Only flip the card when CVC input is focused
        setIsFlipped(isCVCFocused);
    }, [isCVCFocused]);

    return (
        <BringzzPageContainer
            className="bg-white"
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: <BringzzText tag='h2'>Add Credit Card</BringzzText>
            }}
            footer={
                <div className='pb-28 px-6'>
                    <button
                        className="w-full py-4 text-sm font-medium text-midnight-blue bg-magic-lilac rounded-full disabled:opacity-50"
                        onClick={handleSave}
                        disabled={validateCard() || isSaving}
                    >
                        <BringzzText
                            tag='h3'
                            className='text-normal whitespace-pre-line truncate'
                        >
                            {isSaving ? 'Saving...' : 'Save card'}
                        </BringzzText>
                    </button>
                </div>
            }
        >
            <div className='relative flex justify-center bg-magic-lilac/20'>
                <div className='relative bg-white w-full bg-cover bg-center flex flex-col'>
                    <div className='flex-grow flex flex-col py-8 px-4'>
                        <div className='z-10 flex flex-col'>
                            <div className='pl-2 flex flex-col space-y-6 md:max-w-lg lg:max-w-xl xl:max-w-2xl'>
                                <BringzzText tag="h3" className="font-normal">
                                    Please enter your credit card information
                                </BringzzText>
                            </div>
                            <div className='mt-6 space-y-6'>
                                <BringzzInput
                                    label="Credit Card"
                                    placeholder="XXXX XXXX XXXX XXXX"
                                    defaultValue={cardNumber}
                                    icon={{
                                        left: (
                                            <BringzzIcon
                                                icon={cardType ? `Icon${cardType.charAt(0).toUpperCase() + cardType.slice(1)}` : "IconCreditCard"}
                                                folder="LineIcons"
                                                className={`h-6 w-6 ${cardType ? 'text-magic-lilac' : ''}`}
                                                size="24"
                                            />
                                        ),
                                        right: (
                                            <BringzzIcon
                                                icon="IconCameraAlt1"
                                                folder="LineIcons"
                                                className="h-6 w-6"
                                                size="24"
                                            />
                                        ),
                                    }}
                                    onChange={(e, isValid, newValue) => {
                                        const formatted = formatCardNumber(newValue);
                                        if (validateCardNumber(formatted)) {
                                            setCardNumber(formatted);
                                        }
                                    }}
                                    onFocus={() => setIsCVCFocused(false)}
                                />
                                <div className='flex gap-2'>
                                    <BringzzInput
                                        label="Exp. Date"
                                        placeholder="MM/YY"
                                        pattern="^(0[1-9]|1[0-2])\/([0-9]{2})$"
                                        onChange={(e, isValid, newValue) => {
                                            if (isValid) setCardExpDate(newValue)
                                        }}
                                        onFocus={() => setIsCVCFocused(false)}
                                    />
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            CVC
                                        </label>
                                        <div className="relative rounded-md shadow-sm">
                                            <input
                                                type="text"
                                                className="block w-full rounded-md border-gray-300 pl-3 pr-10 focus:border-magic-lilac focus:ring-magic-lilac sm:text-sm h-10"
                                                placeholder="123"
                                                value={cardCVC}
                                                onChange={handleCVCChange}
                                                onFocus={() => setIsCVCFocused(true)}
                                                onBlur={() => setIsCVCFocused(false)}
                                                maxLength={4}
                                            />
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                <BringzzIcon
                                                    icon="IconLock"
                                                    folder="LineIcons"
                                                    className="h-5 w-5 text-gray-400"
                                                    size="20"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <BringzzInput
                                    label="Card holder"
                                    placeholder="Name on card"
                                    pattern="^[A-Za-z ]+$"
                                    onChange={(e, isValid, newValue) => {
                                        if (isValid) setCardHolder(newValue)
                                    }}
                                    onFocus={() => setIsCVCFocused(false)}
                                />
                            </div>
                            <div className='perspective-1000 mt-4 transition-transform duration-500 transform-gpu'>
                                <div className={`relative w-full h-56 transition-transform duration-700 transform-gpu preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
                                    {/* Front of card */}
                                    <div className='absolute w-full h-full backface-hidden'>
                                        <div className='flex w-full h-full'>
                                            <div className='flex flex-col justify-between bg-gradient-to-br from-magic-lilac via-purple-500 to-magic-lilac w-3/4 rounded-l-2xl p-6 shadow-xl'>
                                                <div className='flex justify-between items-center'>
                                                    <BringzzText tag='h2' className="font-normal text-white">
                                                        Bringzz.
                                                    </BringzzText>
                                                    {cardType && (
                                                        <BringzzIcon
                                                            icon={`Icon${cardType.charAt(0).toUpperCase() + cardType.slice(1)}`}
                                                            folder="LineIcons"
                                                            className="text-white"
                                                            size="32"
                                                        />
                                                    )}
                                                </div>
                                                <div className='flex flex-col justify-end space-y-4'>
                                                    <BringzzText tag='h2' className="font-normal text-white tracking-wider">
                                                        {cardNumber ? cardNumber : "XXXX XXXX XXXX XXXX"}
                                                    </BringzzText>
                                                    <div className='flex justify-between items-center'>
                                                        <BringzzText tag='h4' className="font-normal text-white uppercase">
                                                            {cardHolder || "CARD HOLDER"}
                                                        </BringzzText>
                                                        <BringzzText tag='h4' className="font-normal text-white">
                                                            {cardExpDate || "MM/YY"}
                                                        </BringzzText>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='flex justify-center items-center bg-gradient-to-br from-slate-50 via-white to-slate-100 w-1/4 rounded-r-2xl shadow-xl relative overflow-hidden'>
                                                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-slate-50/50 to-slate-100/50"></div>
                                                <BringzzIcon
                                                    icon='IconSignal'
                                                    folder='CustomIcons'
                                                    className='text-slate-300 relative z-10 transform hover:scale-110 transition-transform duration-300'
                                                    size='32'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {/* Back of card */}
                                    <div className='absolute w-full h-full backface-hidden rotate-y-180'>
                                        <div className='flex w-full h-full'>
                                            <div className='flex flex-col justify-between bg-gradient-to-br from-magic-lilac via-purple-500 to-magic-lilac w-3/4 rounded-l-2xl p-6 shadow-xl'>
                                                <div className='w-full h-12 bg-slate-800 mt-4'></div>
                                                <div className='flex justify-end items-center mt-4'>
                                                    <div className='bg-white px-4 py-2 rounded'>
                                                        <BringzzText tag='h4' className="font-normal text-slate-800">
                                                            {cardCVC || "CVC"}
                                                        </BringzzText>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='flex justify-center items-center bg-gradient-to-br from-slate-50 via-white to-slate-100 w-1/4 rounded-r-2xl shadow-xl relative overflow-hidden'>
                                                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-slate-50/50 to-slate-100/50"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .perspective-1000 {
                    perspective: 1000px;
                }
                .preserve-3d {
                    transform-style: preserve-3d;
                }
                .backface-hidden {
                    backface-visibility: hidden;
                }
                .rotate-y-180 {
                    transform: rotateY(180deg);
                }
            `}</style>
        </BringzzPageContainer>
    );
};

export default AddCardForm; 